/**
 * Handover tab
 *
 * Lists completed installations due their handover pack (stages "In Progress
 * (Invoiced)" / "(Handover)" plus Completed not yet released) and provides a
 * per-customer workspace to collate the standard handover documents:
 *   - upload a file against each checklist slot,
 *   - tag a document already on the customer record,
 *   - free-upload extra files.
 * Docs sit hidden from the portal until the final invoice is paid, then are
 * released (auto on payment, or manually here) into the customer's "handover"
 * document folder and published to their portal.
 */
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  PackageCheck, Loader2, Search, CheckCircle2, Upload, Eye, X, Send,
  CircleDashed, MapPin, FileText, PoundSterling, Link2,
} from 'lucide-react';
import { toast } from 'sonner';

const STAGE_LABEL = {
  in_progress_invoiced: 'Installed (Invoiced)',
  in_progress_handover: 'Handover',
  completed: 'Completed',
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const PaidBadge = ({ paid }) => (
  paid
    ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200" data-testid="paid-badge"><PoundSterling className="w-3 h-3 mr-1" />Final paid</Badge>
    : <Badge variant="outline" className="border-amber-300 text-amber-700" data-testid="outstanding-badge"><PoundSterling className="w-3 h-3 mr-1" />Final outstanding</Badge>
);

export default function HandoverPage() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/handover/list');
      setRows(data.rows || []);
    } catch {
      toast.error('Could not load handovers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.name || '').toLowerCase().includes(q) ||
      (r.address || '').toLowerCase().includes(q) ||
      (r.postcode || '').toLowerCase().includes(q));
  }, [rows, query]);

  const readyCount = rows.filter((r) => r.final_invoice_paid).length;

  return (
    <Layout>
      <div className="space-y-6" data-testid="handover-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
              <PackageCheck className="w-6 h-6 text-sky-600" /> Handover
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Completed installs due their handover pack. Collate the documents here — once the final invoice is paid they're filed in the customer's “handover” folder and published to their portal.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-sm">{rows.length} due</Badge>
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-sm">{readyCount} ready to release</Badge>
          </div>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <CardTitle className="text-base">Installations</CardTitle>
              <div className="relative w-72 max-w-full">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="handover-search"
                  placeholder="Search name, address, postcode…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-16 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
            ) : filtered.length === 0 ? (
              <div className="py-16 text-center text-slate-500" data-testid="handover-empty">
                <PackageCheck className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                No installations are awaiting handover.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500 border-b">
                      <th className="px-3 py-2.5 font-medium">Customer</th>
                      <th className="px-3 py-2.5 font-medium">Stage</th>
                      <th className="px-3 py-2.5 font-medium">Install date</th>
                      <th className="px-3 py-2.5 font-medium">Pack progress</th>
                      <th className="px-3 py-2.5 font-medium">Final invoice</th>
                      <th className="px-3 py-2.5 font-medium text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filtered.map((r) => (
                      <tr key={r.customer_id} className="hover:bg-slate-50" data-testid={`handover-row-${r.customer_id}`}>
                        <td className="px-3 py-3">
                          <div className="font-medium text-slate-900">{r.name}</div>
                          <div className="text-xs text-slate-500 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />{[r.address, r.postcode].filter(Boolean).join(', ') || '—'}
                          </div>
                        </td>
                        <td className="px-3 py-3"><Badge variant="outline">{STAGE_LABEL[r.status] || r.status}</Badge></td>
                        <td className="px-3 py-3 text-slate-600">{fmtDate(r.install_date)}</td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-24 h-2 rounded-full bg-slate-100 overflow-hidden">
                              <div className="h-full bg-sky-500" style={{ width: `${(r.filled_slots / r.total_slots) * 100}%` }} />
                            </div>
                            <span className="text-xs text-slate-600">{r.filled_slots}/{r.total_slots}</span>
                          </div>
                        </td>
                        <td className="px-3 py-3"><PaidBadge paid={r.final_invoice_paid} /></td>
                        <td className="px-3 py-3 text-right">
                          <Button size="sm" variant="outline" onClick={() => setOpenId(r.customer_id)} data-testid={`open-handover-${r.customer_id}`}>
                            Collate
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {openId && (
        <HandoverDetailDialog
          customerId={openId}
          onClose={() => setOpenId(null)}
          onChanged={load}
        />
      )}
    </Layout>
  );
}

function HandoverDetailDialog({ customerId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const fileInputs = useRef({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/handover/${customerId}`);
      setData(data);
    } catch {
      toast.error('Could not load handover details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customerId]);

  const handleUpload = async (slot, file) => {
    if (!file) return;
    setBusy(true);
    const t = toast.loading('Uploading…');
    try {
      const fd = new FormData();
      fd.append('slot', slot);
      fd.append('file', file);
      await api.post(`/handover/${customerId}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document added', { id: t });
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed', { id: t });
    } finally {
      setBusy(false);
    }
  };

  const handleTagExisting = async (slot, documentId) => {
    if (!documentId) return;
    setBusy(true);
    try {
      await api.post(`/handover/${customerId}/tag-existing`, { document_id: documentId, slot });
      toast.success('Document added to handover pack');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not tag document');
    } finally {
      setBusy(false);
    }
  };

  const handleUntag = async (documentId) => {
    setBusy(true);
    try {
      await api.post(`/handover/${customerId}/untag`, { document_id: documentId });
      toast.success('Removed from handover pack');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove document');
    } finally {
      setBusy(false);
    }
  };

  const handleRelease = async () => {
    if (!window.confirm('Release the handover pack to the customer portal? This publishes all collated documents to the customer.')) return;
    setReleasing(true);
    try {
      const { data } = await api.post(`/handover/${customerId}/release`);
      toast.success(`Handover released — ${data.documents} document(s) published`);
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Release failed');
    } finally {
      setReleasing(false);
    }
  };

  const viewDoc = (docId) => {
    const base = api.defaults.baseURL || '';
    window.open(`${base}/customers/${customerId}/documents/${docId}/preview`, '_blank');
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="handover-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PackageCheck className="w-5 h-5 text-sky-600" />
            {data?.name || 'Handover'}
          </DialogTitle>
          <DialogDescription>
            {[data?.address, data?.postcode].filter(Boolean).join(', ')}
            {data?.install_date ? ` · Installed ${fmtDate(data.install_date)}` : ''}
          </DialogDescription>
        </DialogHeader>

        {loading || !data ? (
          <div className="py-16 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : (
          <div className="space-y-5">
            <div className="flex items-center gap-2 flex-wrap">
              <PaidBadge paid={data.final_invoice_paid} />
              {data.released
                ? <Badge className="bg-sky-100 text-sky-700 border-sky-200">Released {fmtDate(data.released_at)}</Badge>
                : <Badge variant="outline" className="text-slate-500">Not yet released</Badge>}
            </div>

            {/* Checklist */}
            <div className="space-y-2">
              {data.checklist.map((item) => {
                const has = item.documents.length > 0;
                return (
                  <div key={item.slot} className="border rounded-lg p-3" data-testid={`slot-${item.slot}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2 min-w-0">
                        {has ? <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" /> : <CircleDashed className="w-4 h-4 text-slate-300 flex-shrink-0" />}
                        <span className="font-medium text-slate-800">{item.label}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          ref={(el) => (fileInputs.current[item.slot] = el)}
                          type="file"
                          className="hidden"
                          data-testid={`file-input-${item.slot}`}
                          onChange={(e) => { handleUpload(item.slot, e.target.files?.[0]); e.target.value = ''; }}
                        />
                        <Button size="sm" variant="outline" disabled={busy} onClick={() => fileInputs.current[item.slot]?.click()} data-testid={`upload-${item.slot}`}>
                          <Upload className="w-3.5 h-3.5 mr-1" /> Upload
                        </Button>
                        <select
                          className="text-xs border rounded-md px-2 py-1.5 bg-white max-w-[160px]"
                          disabled={busy || data.taggable_documents.length === 0}
                          defaultValue=""
                          data-testid={`tag-existing-${item.slot}`}
                          onChange={(e) => { handleTagExisting(item.slot, e.target.value); e.target.value = ''; }}
                        >
                          <option value="" disabled>Tag existing…</option>
                          {data.taggable_documents.map((d) => (
                            <option key={d.id} value={d.id}>{d.filename}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {has && (
                      <div className="mt-2 space-y-1">
                        {item.documents.map((d) => (
                          <div key={d.id} className="flex items-center justify-between text-sm bg-slate-50 rounded px-2 py-1" data-testid={`doc-${d.id}`}>
                            <span className="flex items-center gap-1.5 min-w-0 truncate">
                              <FileText className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                              <span className="truncate">{d.filename}</span>
                            </span>
                            <span className="flex items-center gap-1 flex-shrink-0">
                              <Button size="icon" variant="ghost" className="h-7 w-7" title="View" onClick={() => viewDoc(d.id)} data-testid={`view-${d.id}`}><Eye className="w-3.5 h-3.5" /></Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600" title="Remove" disabled={busy} onClick={() => handleUntag(d.id)} data-testid={`remove-${d.id}`}><X className="w-3.5 h-3.5" /></Button>
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Extra / other documents */}
            <div className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-slate-800 text-sm flex items-center gap-2"><Link2 className="w-4 h-4 text-slate-400" /> Other handover files</span>
                <div>
                  <input
                    ref={(el) => (fileInputs.current.other = el)}
                    type="file" className="hidden" data-testid="file-input-other"
                    onChange={(e) => { handleUpload('other', e.target.files?.[0]); e.target.value = ''; }}
                  />
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => fileInputs.current.other?.click()} data-testid="upload-other">
                    <Upload className="w-3.5 h-3.5 mr-1" /> Add file
                  </Button>
                </div>
              </div>
              {data.extra_documents.length === 0 ? (
                <p className="text-xs text-slate-400">None added.</p>
              ) : (
                <div className="space-y-1">
                  {data.extra_documents.map((d) => (
                    <div key={d.id} className="flex items-center justify-between text-sm bg-slate-50 rounded px-2 py-1">
                      <span className="flex items-center gap-1.5 min-w-0 truncate"><FileText className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" /><span className="truncate">{d.filename}</span></span>
                      <span className="flex items-center gap-1 flex-shrink-0">
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => viewDoc(d.id)}><Eye className="w-3.5 h-3.5" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600" disabled={busy} onClick={() => handleUntag(d.id)}><X className="w-3.5 h-3.5" /></Button>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Release */}
            <div className="flex items-center justify-between gap-3 pt-1 flex-wrap">
              <p className="text-xs text-slate-500 max-w-md">
                {data.final_invoice_paid
                  ? 'Final invoice is paid — you can release the pack to the customer portal now.'
                  : 'Release unlocks once the final invoice is paid (it then publishes automatically too).'}
              </p>
              <Button
                onClick={handleRelease}
                disabled={releasing || !data.final_invoice_paid || data.released}
                data-testid="release-handover-btn"
              >
                {releasing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                {data.released ? 'Released' : 'Release to customer'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
